import React from 'react';
import { Phone, Wrench, Shield, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

import SeoNav from '../components/seo/SeoNav';
import CheckList from '../components/seo/CheckList';
import FAQSection from '../components/seo/FAQSection';
import AvisSection from '../components/seo/AvisSection';
import { CtaSection, SeoFooter } from '../components/seo/CtaFooter';

const FAQ = [
  { q: "Combien de temps pour changer un écran iPhone ?", r: "Le remplacement d'un écran iPhone prend environ 30 minutes dans notre atelier à Saint-Chamond. Ce délai inclut le démontage, le remplacement de l'écran et tous les tests de fonctionnement (tactile, Face ID, luminosité)." },
  { q: "L'écran de remplacement est-il de bonne qualité ?", r: "Oui, nous utilisons des écrans OLED premium de très haute qualité, identiques aux écrans d'origine en termes de couleurs, luminosité et réactivité tactile. Tous nos écrans sont testés avant installation. Nous proposons également des écrans d'origine Apple sur demande." },
  { q: "Face ID fonctionnera-t-il après le changement d'écran ?", r: "Oui, absolument. Face ID continuera de fonctionner normalement après le changement d'écran car nous prenons soin de transférer la caméra TrueDepth de votre ancien écran vers le nouvel écran. Nous testons systématiquement Face ID après chaque réparation." },
  { q: "Quelle garantie sur le changement d'écran iPhone ?", r: "Tous nos changements d'écran iPhone sont garantis 3 mois pièce et main d'œuvre. Si vous rencontrez le moindre problème avec l'écran remplacé (tactile, affichage, pixels morts), nous intervenons gratuitement pendant toute la période de garantie." },
  { q: "Mes données seront-elles conservées ?", r: "Oui, toutes vos données restent intactes lors du changement d'écran. Nous ne touchons qu'à la partie matérielle (écran) sans accéder au contenu de votre iPhone. Vos photos, contacts, applications et messages sont conservés." },
  { q: "Combien coûte le changement d'écran d'iPhone 13 ?", r: "Le tarif dépend de la qualité d'écran choisie (OLED premium ou origine). Le devis inclut la pièce, la main d'œuvre et la garantie 3 mois. Appelez-nous ou écrivez-nous sur WhatsApp pour un devis gratuit et immédiat." },
  { q: "Peut-on changer juste la vitre sans l'écran ?", r: "Non, sur les iPhone récents (depuis iPhone X), l'écran et la vitre tactile sont fusionnés et ne peuvent pas être séparés. Il faut obligatoirement remplacer l'ensemble écran complet. Cette méthode garantit un résultat optimal et durable." },
  { q: "Faut-il prendre rendez-vous pour changer l'écran ?", r: "Non, vous pouvez venir directement, mais nous recommandons de prendre rendez-vous ou d'appeler avant pour garantir la disponibilité de l'écran pour votre modèle d'iPhone. Cela évite aussi l'attente. Contactez-nous au 07.73.60.15.21." },
];

const AVIS_PAGE = [
  { text: "Écran de mon iPhone 13 remplacé en 30 minutes chrono ! L'écran OLED est de très bonne qualité, les couleurs sont magnifiques. Très content du service, je recommande.", name: "Pierre R.", ville: "Saint-Chamond" },
  { text: "Mon iPhone 12 avait l'écran complètement cassé. Changé le jour même avec garantie 3 mois. Le tactile fonctionne parfaitement. Excellent rapport qualité-prix !", name: "Julie M.", ville: "Saint-Chamond" },
  { text: "Service ultra rapide ! J'avais besoin de mon iPhone rapidement et l'écran a été changé en 40 minutes. Face ID fonctionne toujours. Parfait !", name: "David L.", ville: "Saint-Chamond" },
];

const ChangementEcranIPhoneSaintChamond = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoNav />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a2332] via-[#1e2a3d] to-[#1a2332] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Changement écran iPhone Saint-Chamond
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Écran iPhone cassé ? Remplacement en 30 minutes avec écran OLED premium et garantie 3 mois
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a href="tel:0773601521" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                <Phone className="w-5 h-5 mr-2" />
                Appeler maintenant
              </Button>
            </a>
            <Link to="/rendez-vous" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                Prendre rendez-vous
              </Button>
            </Link>
            <a href="https://wa.me/33773601521" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">30 min</div>
              <div className="text-sm text-gray-300">Réparation ultra-rapide</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">3 mois</div>
              <div className="text-sm text-gray-300">Garantie totale</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Wrench className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">OLED</div>
              <div className="text-sm text-gray-300">Écran premium</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <CheckCircle className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">100%</div>
              <div className="text-sm text-gray-300">Testé</div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Remplacement d'écran iPhone à Saint-Chamond – Service express</h2>
            
            <p className="text-gray-700 mb-4">
              Votre <strong>écran iPhone est cassé, fissuré ou ne répond plus au tactile</strong> ? Suro Phone est le spécialiste du <strong>changement d'écran iPhone à Saint-Chamond</strong>. Nous intervenons en 30 minutes seulement pour remplacer l'écran de votre iPhone, quel que soit le modèle : iPhone 15 Pro Max, iPhone 14, iPhone 13, iPhone 12, iPhone 11, iPhone XR, iPhone SE, iPhone 8 et tous les autres modèles Apple.
            </p>

            <p className="text-gray-700 mb-4">
              Un écran iPhone cassé est l'une des pannes les plus fréquentes. Que ce soit suite à une chute, un choc ou un impact, l'écran peut se fissurer, se briser complètement, afficher des taches noires, présenter des lignes verticales ou horizontales, ou encore ne plus répondre au tactile. Chez Suro Phone, nous disposons d'un <strong>stock permanent d'écrans iPhone OLED premium</strong> qui nous permet d'intervenir immédiatement sans délai d'attente. Notre atelier à Saint-Chamond est équipé des outils professionnels Apple pour garantir un remplacement d'écran dans les règles de l'art.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Types d'écrans iPhone proposés</h3>
            
            <p className="text-gray-700 mb-4">
              Nous proposons plusieurs qualités d'<strong>écrans de remplacement iPhone</strong> pour s'adapter à tous les budgets. L'écran <strong>LCD premium</strong> offre un excellent rapport qualité-prix avec des couleurs fidèles et un tactile réactif. L'<strong>écran OLED</strong> (disponible sur iPhone X et modèles supérieurs) reproduit parfaitement les couleurs vives, le noir profond et la luminosité éclatante de l'écran d'origine. Enfin, pour les clients les plus exigeants, nous pouvons commander des <strong>écrans d'origine Apple</strong> avec un délai de 24-48h.
            </p>

            <p className="text-gray-700 mb-4">
              Tous nos écrans de remplacement iPhone sont testés avant installation pour vérifier leur qualité. Après la pose, nous effectuons une batterie complète de tests : réactivité tactile sur toute la surface, reconnaissance Face ID, luminosité automatique, True Tone (si compatible), 3D Touch ou Haptic Touch selon le modèle, et affichage des couleurs. Chaque <strong>changement d'écran iPhone à Saint-Chamond</strong> est garanti 3 mois pièce et main d'œuvre.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Le processus de changement d'écran iPhone</h3>
            
            <p className="text-gray-700 mb-4">
              Le remplacement d'un écran iPhone est une opération délicate qui nécessite expertise et précision. Nos techniciens commencent par retirer les deux vis pentalobe situées en bas de l'iPhone, puis soulèvent délicatement l'écran cassé en faisant attention aux nappes connectées à la carte mère. Après avoir débranché les connecteurs d'écran, de tactile et de Face ID, nous nettoyons le châssis de l'iPhone pour éliminer les résidus de verre.
            </p>

            <p className="text-gray-700 mb-4">
              Nous installons ensuite le nouvel écran OLED ou LCD premium en reconnectant soigneusement toutes les nappes. Les plaques métalliques de protection sont remises en place et l'écran est refermé. Avant de vous rendre votre iPhone, nous effectuons tous les tests de fonctionnement : tactile, luminosité, Face ID, affichage. Cette procédure professionnelle de <strong>remplacement d'écran iPhone</strong> prend environ 30 minutes et garantit un résultat optimal.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Devis gratuit pour votre écran iPhone à Saint-Chamond</h3>
            
            <p className="text-gray-700 mb-4">
              Le tarif de votre <strong>remplacement d'écran iPhone</strong> dépend de votre modèle (iPhone SE, 8, XR, 11, 12, 13, 14, 15…) et de la qualité d'écran choisie. Nous vous communiquons un devis gratuit et précis en quelques minutes par téléphone ou WhatsApp, avant toute intervention et sans engagement.
            </p>

            <CheckList items={[
              { titre: "Remplacement express :", texte: "Écran iPhone changé en 30 minutes dans notre atelier à Saint-Chamond." },
              { titre: "Écrans OLED premium :", texte: "Qualité identique à l'origine avec couleurs vives et noir profond." },
              { titre: "Tests complets :", texte: "Tactile, Face ID, luminosité, True Tone vérifiés avant restitution." },
              { titre: "Garantie 3 mois :", texte: "Tous nos changements d'écran iPhone sont garantis 3 mois pièce et main d'œuvre." },
              { titre: "Devis immédiat :", texte: "Tarif communiqué par téléphone selon votre modèle d'iPhone avant intervention." },
              { titre: "Stock disponible :", texte: "Écrans disponibles en stock pour la majorité des modèles iPhone récents." },
            ]} />

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Pourquoi faire changer l'écran de votre iPhone rapidement ?</h3>
            
            <p className="text-gray-700 mb-4">
              Un écran iPhone fissuré ou cassé n'est pas seulement inesthétique, il peut poser plusieurs problèmes. Les fissures peuvent s'aggraver avec le temps et se propager sur toute la surface de l'écran. Des éclats de verre peuvent se détacher et vous blesser les doigts. L'eau et la poussière peuvent s'infiltrer par les fissures et endommager les composants internes de l'iPhone. Le tactile peut devenir moins réactif ou cesser complètement de fonctionner dans certaines zones.
            </p>

            <p className="text-gray-700 mb-4">
              De plus, un écran cassé dégrade considérablement l'expérience d'utilisation de votre iPhone. Il est difficile de lire correctement, les couleurs sont altérées, et la luminosité peut être réduite. Face ID peut ne plus fonctionner si la caméra TrueDepth est endommagée. En faisant <strong>remplacer rapidement l'écran de votre iPhone à Saint-Chamond</strong>, vous évitez l'aggravation de la panne et retrouvez un téléphone comme neuf pour un coût raisonnable.
            </p>

            <p className="text-gray-700 mb-4">
              Pour un <strong>changement d'écran iPhone professionnel et rapide à Saint-Chamond</strong>, contactez Suro Phone au 07.73.60.15.21. Nous intervenons du lundi au samedi avec ou sans rendez-vous. Devis gratuit par téléphone et intervention en 30 minutes pour retrouver un iPhone comme neuf. Service disponible pour tous les modèles d'iPhone avec garantie 3 mois. N'attendez plus pour redonner une seconde vie à votre iPhone !
            </p>
          </div>
        </div>
      </section>

      {/* Price Examples Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Modèles d'iPhone pris en charge</h2>
            <p className="text-lg text-gray-600">Devis gratuit et immédiat pour chaque modèle</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">iPhone récents</h3>
                <ul className="space-y-3">
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone 15 / 15 Plus</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone 14 / 14 Plus</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone 13 / 13 Pro</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">iPhone 12 / 12 Pro</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow border-2 border-blue-500">
              <CardContent className="p-6">
                <div className="bg-blue-500 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-3">Populaire</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">iPhone standards</h3>
                <ul className="space-y-3">
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone 11</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone XR</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone X / XS</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">iPhone SE (2020/2022)</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">iPhone anciens</h3>
                <ul className="space-y-3">
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone 8 / 8 Plus</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone 7 / 7 Plus</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center pb-2 border-b">
                    <span className="text-gray-700">iPhone 6S / 6S Plus</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                  <li className="flex justify-between items-center">
                    <span className="text-gray-700">iPhone 6 / 6 Plus</span>
                    <span className="font-bold text-blue-600">Sur devis</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-gray-600 mb-4">Écran OLED premium • Garantie 3 mois incluse • Devis gratuit</p>
            <Link to="/rendez-vous">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg rounded-lg transition-colors">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <AvisSection titre="Avis clients" avis={AVIS_PAGE} sousTitre="Changement d'écran iPhone à Saint-Chamond" />

      <FAQSection titre="Questions fréquentes" items={FAQ} sousTitre="Changement d'écran iPhone" />

      <CtaSection titre="Écran iPhone cassé ? On le change en 30 min !" texte="Contactez-nous pour un devis gratuit et un changement d'écran rapide à Saint-Chamond" />

      {/* Footer */}
      <SeoFooter texte="© 2026 Suro Phone - Changement écran iPhone Saint-Chamond - Tous droits réservés" />
    </div>
  );
};

export default ChangementEcranIPhoneSaintChamond;